/**
 * Input — shadcn Input 어댑터
 *
 * 기존 @/ui Input API(label/error/helperText/required/size)를 유지하고
 * 내부 렌더링은 shadcn Input에 위임한다.
 * forwardRef로 외부에서 ref 접근을 지원한다.
 */

import { forwardRef } from 'react'
import type { InputHTMLAttributes } from 'react'
import { Input as ShadInput } from '@/components/ui/input'
import { cn } from './cn'

// size 는 native input 속성과 충돌하므로 Omit 처리
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** 입력 크기. 기본값 md */
  size?: 'sm' | 'md' | 'lg'
  /** 필드 레이블 텍스트 */
  label?: string
  /** 에러 메시지 — 표시 시 입력 테두리가 빨간색으로 변함 */
  error?: string
  /** 보조 안내 텍스트 */
  helperText?: string
  /** 필수 필드 표시 (레이블 옆 * 표시) */
  required?: boolean
}

// size prop → shadcn 높이 클래스 매핑
const sizeClass: Record<NonNullable<InputProps['size']>, string> = {
  sm: 'h-8',
  md: 'h-9',
  lg: 'h-10',
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', label, error, helperText, required, className, id, ...props },
  ref,
) {
  // id가 없으면 label htmlFor 연결이 안 되므로 자동 생성
  const inputId = id ?? (label ? `input-${Math.random().toString(36).slice(2, 9)}` : undefined)

  // className은 기존 계약대로 외부 wrapper div에 적용 (레이블+인풋+헬퍼 그룹 전체 여백 제어)
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label className="text-sm font-medium text-foreground" htmlFor={inputId}>
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </label>
      )}
      <ShadInput
        ref={ref}
        id={inputId}
        // aria-invalid 으로 shadcn 내장 에러 스타일(border-destructive) 활성화
        aria-invalid={!!error || undefined}
        className={sizeClass[size]}
        {...props}
      />
      {error && <span className="text-xs text-destructive">{error}</span>}
      {!error && helperText && <span className="text-xs text-muted-foreground">{helperText}</span>}
    </div>
  )
})
